import {
  useActionsOmniSpeakToText,
  useLazyOmniSpeakToTexts,
  useProfile,
  useReceiveOmniSpeakToText,
  useScrollOmniSpeakToText
} from 'hooks';
import { find } from 'lodash';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { offReceiveSpeakToText, onReceiveSpeakToText, useReadOmniChatMessagesMutation } from 'services';
import { CallSessionDto, SpeakToTextDto, SpeakToTextPosition } from 'types';

type Variables = {
  systemMessText?: string;
};

export const MAX_WIDTH_MESSAGE_OMNI_HISTORIES = 632;

export type AIAgentCallChatContextProps = {
  messages: SpeakToTextDto[];
  positions: SpeakToTextPosition[];
  messageListRef: React.RefObject<HTMLDivElement> | null;
  messagesBoxRef: React.RefObject<HTMLDivElement> | null;
  callSession: CallSessionDto;
  hasMore: boolean;
  hasNew: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isAtBottomScroll: boolean;
  onGetNewerMess: () => void;
  onGetOlderMess: () => void;
  scrollToEndMessage: () => void;
  displayIndex: {
    start: number;
    end: number;
  };
  variables?: Variables;
  isShowActions?: boolean;

  chatBoxWidth?: number;
};

export const AIAgentCallChatContext = createContext<AIAgentCallChatContextProps | undefined>(undefined);

export const AIAgentCallChatProvider: React.FC<{
  children: React.ReactNode;
  callSession: CallSessionDto;
  endpointName?: string;
  setCallSession?: (value: CallSessionDto) => void;
  variables?: Variables;
  isShowActions?: boolean;
  isRead?: boolean;
  chatBoxWidth?: number;
  isShowName?: boolean;
}> = ({
  children,
  setCallSession,
  variables,
  isShowActions,
  isRead = true,
  isShowName = true,
  chatBoxWidth,
  callSession
}) => {
  const { callSessionId, callUuid } = callSession;
  const { profile } = useProfile();
  const [loading, setLoading] = useState(false);

  const [onReadMess] = useReadOmniChatMessagesMutation();

  // const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
  const isAction = false;

  const {
    fetchData: fetchMessage,
    data: messages,
    isFetching,
    isLoading,
    hasMore,
    hasNew,
    getOlderMess,
    getNewerMess,
    endpointName: endpointNameMessages
  } = useLazyOmniSpeakToTexts(callSession, {
    callSessionId: callSession.callSessionId
  });

  const {
    isAtBottomScroll,
    displayIndex,
    positionsRef,
    changeHeightChatList,
    messagesBoxRef,
    messageListRef,
    scrollToEndMessage
  } = useScrollOmniSpeakToText({
    callSession,
    fetchMessage,
    hasNew,
    setLoading,
    isRead,
    maxMessageWidth: chatBoxWidth,
    isShowName
  });

  const {
    handleReceiveNewOmniSpeakToText,
    handleReceiveNewerOmniSpeakToTextList,
    handleReceiveOlderOmniSpeakToTextList
  } = useReceiveOmniSpeakToText(chatBoxWidth, isShowName);

  const { handleUpdateMessage, handleAddMessage } = useActionsOmniSpeakToText(callSessionId, endpointNameMessages);

  const lastMessageRef = useRef<SpeakToTextDto | null>(null);
  const firstMessageRef = useRef<SpeakToTextDto | null>(null);

  useEffect(() => {
    if (callSessionId) {
      onGetMessages();
    }
  }, [callSessionId]);

  useEffect(() => {
    onReceiveSpeakToText((data) => {
      if (data.callSessionId === callSessionId) {
        onReceiveNewMessage(data);
      }
    });
    return () => {
      offReceiveSpeakToText();
    };
  }, [callSessionId, handleAddMessage, hasNew]);

  useEffect(() => {
    lastMessageRef.current = messages[messages.length - 1];
    firstMessageRef.current = messages[0];

    return () => {
      lastMessageRef.current = null;
      firstMessageRef.current = null;
    };
  }, [messages]);

  // const handleReadAllMess = (data: ReadOmniChannelMessageDto) => {
  //   if (isRead) onReadMess(data).unwrap();
  // };

  const onGetMessages = () => {
    fetchMessage({
      callSessionId: callSessionId
    })
      .unwrap()
      .then(async (rs) => {
        positionsRef.current = [];
        const data = await handleReceiveOlderOmniSpeakToTextList(rs.data.rows, null, []);

        if (data) {
          changeHeightChatList(data.height, data.height);
          positionsRef.current = data.positions;
        }
        // handleReadAllMess({
        //   chatSessionId,
        //   messageIds: []
        // });
      });
  };

  const onGetOlderMess = () => {
    if (loading || isFetching || isLoading || !hasMore) return;
    getOlderMess().then(async (rs) => {
      if (rs.data?.newMess) {
        const data = await handleReceiveOlderOmniSpeakToTextList(
          rs.data?.newMess,
          firstMessageRef.current,
          positionsRef.current
        );
        if (data) {
          positionsRef.current = data.positions;
          const currentScroll = messagesBoxRef.current?.scrollTop ?? 0;
          changeHeightChatList(data.height, currentScroll + data.changedHeight);
        }
      }
    });
  };

  const onGetNewerMess = () => {
    if (loading || isFetching || isLoading || !hasNew) return;
    getNewerMess().then(async (rs) => {
      if (rs.data?.newMess?.length) {
        const data = await handleReceiveNewerOmniSpeakToTextList(
          rs.data?.newMess,
          lastMessageRef.current,
          positionsRef.current
        );
        if (data) {
          positionsRef.current = data.positions;
          const currentScroll = messagesBoxRef.current?.scrollTop ?? 0;
          changeHeightChatList(data.height, data.removedHeight ? currentScroll - data.removedHeight : currentScroll);
        }
      }
    });
  };

  const onReceiveNewMessage = async (message: SpeakToTextDto) => {
    if (
      positionsRef.current &&
      find(positionsRef.current, (o) => (o as SpeakToTextPosition).speakToTextId === message.speakToTextId)
    ) {
      handleUpdateMessage(message);
      return;
    }

    // if (
    //   typeof message.senderId === 'number' &&
    //   message.senderId === profile?.userId &&
    //   message.messageType !== MESSAGE_TYPE.SYSTEM
    // )
    //   return;
    if (isAtBottomScroll && !hasNew) {
      const data = await handleReceiveNewOmniSpeakToText(message, lastMessageRef.current, positionsRef.current);
      if (data) {
        positionsRef.current = data.positions;
        const currentScroll = messagesBoxRef.current?.scrollTop ?? 0;
        changeHeightChatList(data.height, data.removedHeight ? currentScroll - data.removedHeight : currentScroll);
        messagesBoxRef.current?.scrollTo({
          top: data.height,
          behavior: 'smooth'
        });
        handleAddMessage(message);
        // handleReadAllMess({ chatSessionId, messageIds: [message.messageId] });
      }
    }

    setCallSession?.({
      ...callSession,
      lastSpeakToTextId: message.speakToTextId
    });
  };
  return (
    <AIAgentCallChatContext.Provider
      value={{
        variables,
        callSession,
        messageListRef,
        messagesBoxRef,
        positions: positionsRef.current,
        displayIndex: {
          start: displayIndex.start,
          end: displayIndex.end
        },
        isAtBottomScroll,
        scrollToEndMessage,

        hasMore,
        hasNew,
        isFetching,
        isLoading,
        messages,
        onGetNewerMess,
        onGetOlderMess,

        isShowActions: isAction,
        chatBoxWidth
      }}
    >
      {/* <Spin className='max-h-full overflow-hidden' spinning={isLoading || isFetching}> */}
      {children}
      {/* </Spin> */}
    </AIAgentCallChatContext.Provider>
  );
};
export default AIAgentCallChatProvider;

export const useAIAgentCallChatContext = (): AIAgentCallChatContextProps => {
  const context = useContext(AIAgentCallChatContext);
  if (!context) {
    throw new Error('useAIAgentCallChatContext must be used within a AIAgentCallChatProvider');
  }
  return context;
};
